import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { User, Anchor, Loader2, Save, Ship, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { UserProfile, UserBoat } from '@regatta-replay/types';

/**
 * The signed-in profile.
 *
 * Written in the viewer palette rather than the light `Card` components it used
 * to use. Those were correct when the whole public site was light; once the
 * shell went dark they read as white boxes pasted onto a dark page — which is
 * exactly how Bir described them.
 *
 * The shared `Card` is deliberately not used: it hard-codes a white surface, so
 * theming it here would mean overriding every one of its classes at the call
 * site. A local panel is shorter and says what it means.
 */

interface ProfileData {
  user: UserProfile;
  boats: {
    owned: UserBoat[];
    crewing: UserBoat[];
    supporting?: UserBoat[];
  };
}

/** One dark surface, so the three sections cannot drift apart. */
function Panel({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-viewer-line/25 bg-gradient-to-b from-viewer-panel2 to-viewer-panel">
      {children}
    </section>
  );
}

function PanelHead({ icon, title, children }: { icon?: React.ReactNode; title: string; children?: React.ReactNode }) {
  return (
    <header className="border-b border-viewer-line/20 px-5 py-4">
      <h2 className="flex items-center gap-2 font-viewer-display text-[17px] font-bold text-viewer-ink">
        {icon}
        {title}
      </h2>
      {children && <p className="mt-1 text-[12.5px] leading-relaxed text-viewer-muted">{children}</p>}
    </header>
  );
}

const FIELD =
  'border-viewer-line/30 bg-viewer-deep/60 text-viewer-ink placeholder:text-viewer-faint focus-visible:ring-viewer-accent/40';

export function ProfilePage() {
  const { user: authUser, login, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsApp, setWhatsApp] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<ProfileData>('/me');
      setProfile(data);
      setName(data.user.name || '');
      setPhone(data.user.phone || '');
      setWhatsApp(data.user.whatsApp || '');
    } catch (err) {
      console.error('[ProfilePage] Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  async function handleSave() {
    setSaving(true);
    try {
      const result = await api.put<{ user: UserProfile }>('/me', {
        name: name.trim(),
        phone: phone.trim(),
        whatsApp: whatsApp.trim(),
      });
      setProfile((prev) => (prev ? { ...prev, user: result.user } : prev));
      // Update auth store so header reflects new name
      if (token) {
        login(token, result.user);
      }
      toast({
        title: 'Profile updated',
        description: 'Your changes have been saved.',
      });
    } catch {
      // toast handled by api helper
    } finally {
      setSaving(false);
    }
  }

  /**
   * Give a boat back.
   *
   * Confirmed first: a claim is how a sailor's races, moments and home page
   * find them, so an accidental tap costs more than it looks like it does. The
   * endpoint (DELETE /me/boats/:boatId) already backs the My Boats modal — this
   * surfaces it where the boats are actually listed.
   */
  async function handleRemove(boatId: string, label: string) {
    if (!window.confirm(`Remove ${label} from your account?\n\nYour races and moments for this boat will stop appearing on your home page. You can claim it again later.`)) {
      return;
    }
    setRemoving(boatId);
    try {
      await api.delete(`/me/boats/${boatId}`);
      // Reload rather than splice: the same boat can appear under two
      // relationships, and the server is the authority on what is left.
      await loadProfile();
      toast({ title: 'Boat removed', description: `${label} is no longer on your account.` });
    } catch (err) {
      toast({
        title: 'Could not remove boat',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRemoving(null);
    }
  }

  const hasChanges =
    profile &&
    (name.trim() !== (profile.user.name || '') ||
      phone.trim() !== (profile.user.phone || '') ||
      whatsApp.trim() !== (profile.user.whatsApp || ''));

  const allBoats = [
    ...(profile?.boats.owned ?? []).map((b) => ({ ...b, relationship: 'Owner' as const })),
    ...(profile?.boats.crewing ?? []).map((b) => ({ ...b, relationship: 'Crew' as const })),
    // Boats they follow without having sailed them.
    ...(profile?.boats.supporting ?? []).map((b) => ({ ...b, relationship: 'Supporter' as const })),
  ];

  return (
    <>
      <Helmet>
        <title>My Profile - RegattaReplay™</title>
      </Helmet>

      <div className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="mb-6 flex items-center gap-2 font-viewer-display text-2xl font-bold text-viewer-ink">
          <User size={24} />
          My Profile
        </h1>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full bg-viewer-panel2" />
            <Skeleton className="h-48 w-full bg-viewer-panel2" />
          </div>
        ) : profile ? (
          <div className="space-y-6">
            <Panel>
              <PanelHead title="Personal Information">
                Update your display name and contact information.
              </PanelHead>
              <div className="space-y-4 px-5 py-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-viewer-muted">Email</Label>
                  <Input
                    id="email"
                    value={profile.user.email}
                    disabled
                    className="border-viewer-line/25 bg-viewer-deep/40 text-viewer-faint"
                  />
                  <p className="text-xs text-viewer-faint">
                    Email cannot be changed. Contact support if you need to update it.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-viewer-muted">Display Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className={FIELD}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-viewer-muted">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={FIELD}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp" className="text-viewer-muted">WhatsApp</Label>
                  <Input
                    id="whatsapp"
                    type="tel"
                    value={whatsApp}
                    onChange={(e) => setWhatsApp(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={FIELD}
                  />
                  <p className="text-xs text-viewer-faint">
                    Used for crew coordination notifications (optional).
                  </p>
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    className="bg-brand-ink text-white hover:bg-brand-dark"
                  >
                    {saving ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Save size={16} className="mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </div>
            </Panel>

            <Panel>
              <PanelHead icon={<Ship size={18} />} title="My Boats">
                Boats you own, crew on or follow.{' '}
                <Link to="/onboarding" className="font-medium text-viewer-accent hover:underline">
                  Claim another boat
                </Link>
                .
              </PanelHead>
              <div className="px-5 py-4">
                {allBoats.length > 0 ? (
                  <ul className="divide-y divide-viewer-line/15">
                    {allBoats.map((boat) => (
                      /* A row, not a link: the remove button lives inside it,
                         and a <button> nested in an <a> is invalid and swallows
                         the click on some browsers. */
                      <li
                        key={`${boat.boatId}-${boat.relationship}`}
                        className="group flex items-center gap-3 py-3"
                      >
                        <Anchor size={16} className="shrink-0 text-viewer-faint transition-colors group-hover:text-viewer-accent" />
                        <Link to={`/boat/${boat.boatId}`} className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-medium text-viewer-ink">
                            {boat.classDisplayName && (
                              <span className="font-normal text-viewer-faint">
                                {boat.classDisplayName} ·{' '}
                              </span>
                            )}
                            {boat.sailCountry} {boat.sailNumber} {boat.boatName}
                          </span>
                          {boat.homeClubName && (
                            <span className="block truncate text-[11px] text-viewer-faint">
                              {boat.homeClubName}
                            </span>
                          )}
                        </Link>
                        <span className="shrink-0 rounded border border-viewer-line/25 px-2 py-0.5 font-viewer-mono text-[10px] uppercase tracking-[.1em] text-viewer-muted">
                          {boat.relationship}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemove(
                              boat.boatId,
                              `${boat.sailCountry ?? ''} ${boat.sailNumber} ${boat.boatName}`.trim(),
                            )
                          }
                          disabled={removing === boat.boatId}
                          aria-label={`Remove ${boat.sailNumber} ${boat.boatName} from your account`}
                          title="Remove from my boats"
                          className="shrink-0 rounded p-1.5 text-viewer-faint transition-colors hover:bg-viewer-coral/10 hover:text-viewer-coral disabled:opacity-40"
                        >
                          {removing === boat.boatId ? (
                            <Loader2 size={15} className="animate-spin" />
                          ) : (
                            <Trash2 size={15} />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="py-8 text-center text-sm text-viewer-muted">
                    <Anchor size={32} className="mx-auto mb-3 opacity-30" />
                    <p>No boats yet.</p>
                    <p className="mt-1">
                      <Link to="/onboarding" className="font-medium text-viewer-accent hover:underline">
                        Find your boat
                      </Link>{' '}
                      in a regatta you sailed.
                    </p>
                  </div>
                )}
              </div>
            </Panel>

            <Panel>
              <PanelHead title="Account" />
              <div className="space-y-2 px-5 py-4 text-sm text-viewer-muted">
                <div className="flex justify-between">
                  <span>Role</span>
                  <span className="font-medium capitalize text-viewer-ink">{profile.user.role}</span>
                </div>
                <div className="flex justify-between">
                  <span>Member since</span>
                  <span className="font-medium text-viewer-ink">
                    {new Date(profile.user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                {profile.user.tosAcceptedAt && (
                  <div className="flex justify-between">
                    <span>Terms accepted</span>
                    <span className="font-medium text-viewer-ink">
                      {new Date(profile.user.tosAcceptedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </div>
            </Panel>
          </div>
        ) : (
          <div className="py-12 text-center text-viewer-muted">
            <p>Unable to load profile.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={loadProfile}>
              Try Again
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
